import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Pause, Play, Radio, Square, Upload } from "lucide-react";
import { toast } from "sonner";
import { errMsg, mediaUrl, useApi } from "../../lib/api";

const MAX_BYTES = 50 * 1024 * 1024;

function useSpaceTracks(api, spaceId, onTracks) {
  const [tracks, setTracks] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/spaces/${spaceId}/tracks`);
      setTracks(data);
      onTracks?.(data);
    } catch (e) { toast.error(errMsg(e, "Couldn't load this room's tracks")); }
  }, [api, spaceId, onTracks]);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    if (file.size > MAX_BYTES) return toast.error("Tracks must be under 50 MB");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name.replace(/\.[^.]+$/, ""));
    setBusy(true);
    try { await api.post(`/spaces/${spaceId}/tracks`, fd); toast.success("Track uploaded"); load(); }
    catch (err) { toast.error(errMsg(err, "Upload failed")); }
    finally { setBusy(false); }
  };
  return { tracks, busy, upload };
}

function usePreview(api) {
  const [preview, setPreview] = useState(null);
  const audio = useRef(null);
  useEffect(() => () => audio.current?.pause(), []);
  const toggle = async (t) => {
    audio.current?.pause();
    if (preview === t.id) return setPreview(null);
    try {
      audio.current = new Audio(await mediaUrl(api, t.id));
      audio.current.onended = () => setPreview(null);
      await audio.current.play();
      setPreview(t.id);
    } catch (err) { toast.error(errMsg(err, "Can't play this track")); }
  };
  return { preview, toggle };
}

const TrackControls = ({ t, onAir, ch, channels, toRoom, onAssign }) => (
  <div className="mt-2 flex items-center gap-2">
    <button onClick={() => toRoom(t)} data-testid={`track-room-play-btn-${t.id}`}
      className={`flex items-center gap-1 rounded-[2px] border px-2 py-1 text-[11px] transition-colors ${onAir ? "border-pink-500 text-pink-300" : "border-zinc-700 text-zinc-300 hover:text-yellow-300"}`}>
      {onAir ? <Square className="h-3 w-3" /> : <Radio className="h-3 w-3" />} {onAir ? "Stop" : "Play to room"}
    </button>
    <span className="mono text-[10px] text-zinc-500">Mixer</span>
    <select value={ch >= 0 ? ch : ""} onChange={(e) => onAssign(t.id, e.target.value === "" ? null : +e.target.value)}
      data-testid={`track-assign-select-${t.id}`} className="flex-1 rounded-[2px] bg-black border border-zinc-800 text-[11px] px-1 py-1">
      <option value="">No channel</option>
      {channels.map((n) => <option key={`ch-${n}`} value={n}>Channel {n + 1}</option>)}
    </select>
  </div>
);

const TrackRow = ({ t, playing, previewing, onPreview, controls }) => (
  <div className="rounded-[3px] border border-[#2d2d38] bg-black/40 p-2.5" data-testid={`track-row-${t.id}`}>
    <div className="flex items-center gap-2">
      <button onClick={() => onPreview(t)} data-testid={`track-preview-btn-${t.id}`} className="grid h-7 w-7 place-items-center rounded-full border border-zinc-700 hover:border-[#39ff6a]">
        {previewing ? <Pause className="h-3.5 w-3.5 text-[#39ff6a]" /> : <Play className="h-3.5 w-3.5" />}
      </button>
      <span className="flex-1 truncate text-sm">{t.title}</span>
      {playing && <span className="mono text-[9px] text-[#39ff6a]">PLAYING</span>}
    </div>
    {controls}
  </div>
);

export const TracksPanel = ({ spaceId, canControl, playback, mixer, onAssign, onTracks }) => {
  const api = useApi();
  const input = useRef(null);
  const { tracks, busy, upload } = useSpaceTracks(api, spaceId, onTracks);
  const { preview, toggle } = usePreview(api);
  const channels = mixer.channels.map((_, n) => n);
  const toRoom = async (t) => {
    const body = playback?.track_id === t.id ? { track_id: null } : { track_id: t.id, playing: true, position: 0 };
    try { await api.post(`/spaces/${spaceId}/playback`, body); } catch (err) { toast.error(errMsg(err)); }
  };
  const pick = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); };

  return (
    <div className="space-y-3" data-testid="tracks-panel">
      <input ref={input} type="file" accept=".mp3,.wav,audio/mpeg,audio/wav" hidden onChange={pick} data-testid="track-file-input" />
      <button onClick={() => input.current.click()} disabled={busy} data-testid="track-upload-btn"
        className="w-full flex items-center justify-center gap-2 rounded-[3px] border border-dashed border-zinc-700 py-3 text-sm text-zinc-300 hover:border-yellow-400 hover:text-yellow-300 transition-colors">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload MP3 or WAV
      </button>
      {tracks.length === 0 && <p className="text-sm text-zinc-500" data-testid="tracks-empty">No shared tracks yet.</p>}
      {tracks.map((t) => {
        const onAir = playback?.track_id === t.id;
        const ch = mixer.channels.findIndex((c) => c.source === `t:${t.id}`);
        return (
          <TrackRow key={t.id} t={t} playing={onAir} previewing={preview === t.id} onPreview={toggle}
            controls={canControl && <TrackControls t={t} onAir={onAir} ch={ch} channels={channels} toRoom={toRoom} onAssign={onAssign} />} />
        );
      })}
    </div>
  );
};
